
import { Component, Prop, Vue, Watch,  } from 'vue-property-decorator';
import { Helper, WorkspaceDetailsParams } from "../models/HelperClasses";
import VatWarning from '../components/shared/VatWarning.vue';
import { mapState } from 'vuex';
import { Workspace } from '../models/Workspace';
import {GetWorkspacesParams, AddWorkspacesParams} from '@/models/StoreClasses'
import { Dictionary } from 'vue-router/types/router';
import { AuthModule } from '@/auth/AuthModule';

@Component({
  components: {VatWarning},
  computed: mapState(["workspaces", "toaster", "authModule"])
})
export default class Workspaces extends Vue {

  public toaster!: string //Is assigned via mapstate
  public workspaces!: Dictionary<Workspace[]> //Is assigned via mapstate
  public authModule!: AuthModule; //Is assigned via mapstate
  private loading: boolean = false;
  private preloading: boolean = false;

  public tableColumns: any = [
    {key: "name", label: "Name", sortable: true},
    {key: "workspaceStatus", label: "Workspace status", sortable: true},
    {key: "vmStatusIcon", label: "VM Status"},
    {key: "budgetStatus", label: "Budget status", sortable: true},
    {key: "budget", label: "Budget", sortable: true},
    {key: "currentSpend", label: "Spent", sortable: true},
    {key: "budgetBar", label: ""},
    {key: "buttons", label: ""}
  ]

  rowClass(item: any, type: string){
    if(type === "row"){
      return item.workspaceStatus === 'Available' || item.workspaceStatus === 'Disabled' ? 'pointer' : '';
    }
    return "";
  }

  formatMoney(money: number){
    return Helper.formatMoney(money);
  }

  goToUpdateScreen(workspaceId: string){    
    this.$router.push({name: "UpdateWorkspace", params: {workspaceId: workspaceId}});
  }

  get userWorkspaces(){
    let key = `workspaces|${this.authModule.userName}`;
    let preloadkey = `preloadworkspaces|${this.authModule.userName}`;
    if (this.workspaces[key])
      return this.workspaces[key];
    else if (this.workspaces[preloadkey])
      return this.workspaces[preloadkey];
  }
 
  get warning(){
    var date = new Date();
    var date2 = new Date(); //Needed because setDate actually changes the date....
    var datePlus14Days = new Date(date.setDate(date.getDate() + 14));
    var workspacesWhichExpireSoon = this.userWorkspaces.filter(ws => {  
      var budgetExpirationDate = new Date(ws.budgetExpirationDate);
      return datePlus14Days > budgetExpirationDate && date2 <= budgetExpirationDate;
    });

    var showExpirationWarning = false;
    if(workspacesWhichExpireSoon && workspacesWhichExpireSoon.length > 0){
      showExpirationWarning = true;
    }
    
    var workspacesWhichWillReachBudgetSoon = this.userWorkspaces.filter(ws => {
      var ratio = ws.currentSpend/ws.budget;
      return ratio >= 0.9 && ratio < 1;
    });

    var showBudgetWarning = false;
    if(workspacesWhichWillReachBudgetSoon && workspacesWhichWillReachBudgetSoon.length > 0){
      showBudgetWarning = true;
    }
    return {
      showExpirationWarning: showExpirationWarning,
      showBudgetWarning: showBudgetWarning
    }
  }

  @Watch("toaster")
  OnPropertyChanged(value: string){
    if(value !== ""){
        this.$swal({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          type: "success",
          text: value,
          allowOutsideClick: true
        });
      this.$store.commit("setToaster", "");
    }
  }

  formatPercentage = (fraction: number) => {
    return `${(fraction*100).toFixed(0)}%`
  }

  getWorkspaceStatus(workspace: Workspace){
    if (workspace.tags == null)
      return workspace.workspaceStatus;
    var status: string;
    if(workspace.workspaceStatus === 'Disabled'){
      var isRemovedAt = new Date(workspace.tags.get("IsRemovedAt") as string);
      var formattedIsRemovedAt = Helper.formatDate(isRemovedAt)
      status = `Disabled, will be removed at ${formattedIsRemovedAt}`;
    } else {
      status = workspace.workspaceStatus;
    }
    return status;
  }

  private details(workspace: Workspace){
    if(workspace.workspaceStatus === 'Available' || workspace.workspaceStatus === 'Disabled'){
      this.$router.push({name: "WorkspaceDetail", params: {resourceGroupName: workspace.resourceGroupName, subscriptionId: workspace.subscriptionId }});
    }
  }

  vmStatusColor(vmState: string) {
    if (vmState == null || this.loading || vmState === "No VMs")
      return {color: "transparent"};
    if (vmState === "VM running")
      return {color: "#3F9C35"};
    if (vmState === "VM deallocated")
      return {color: "#C60C30"};
    else
      return {color: "#EC7A08"};
  }

  vmHoverText(vmState:string) {
    if (vmState == null || this.loading)
      return "";
    if (vmState === "VM running")
      return "One or more VM's are running";
    if (vmState === "VM deallocated")
      return "All VM's are deallocated";
    if (vmState === "VM starting")
      return "A VM is starting";
    if (vmState === "No VMs")
      return "No VM's in this workspace" 
    return vmState;
  }

  async getVmStates(){
    await Promise.all(this.userWorkspaces.map(async ws => {
        var data : WorkspaceDetailsParams = new WorkspaceDetailsParams({
            resourceGroupName: ws.resourceGroupName, 
            subscriptionId: ws.subscriptionId
          });
        await this.$store.dispatch("getWorkspaceVMStatus", data);
        })
      );
    }

  async NavigateToManualIfNoWorkspaces(){
    let preloadkey = `preloadworkspaces|${this.authModule.userName}`;
    let key = `workspaces|${this.authModule.userName}`;
    if(this.workspaces[preloadkey].length === 0 && this.workspaces[key].length === 0 && !this.$store.state.isInitialized){
      this.$store.commit('setIsInitialized');
      this.$router.push("/Documentation?tab=manual");
    }
  }

  async created(){
    this.preloading = true;
    this.loading = true;
    let forceRefresh = this.$route.query.forceRefresh && this.$route.query.forceRefresh === "1" ? true : false;
    let getWorkspacesParams = new GetWorkspacesParams(this.authModule.userName, forceRefresh);
    await this.$store.dispatch("preloadWorkspaces", getWorkspacesParams);
    this.preloading = false;
    let getWorkspacesParams2 = new GetWorkspacesParams(this.authModule.userName, forceRefresh);
    await this.$store.dispatch("getWorkspaces", getWorkspacesParams2);
    this.NavigateToManualIfNoWorkspaces();
    await this.getVmStates();
    this.loading = false;
    let query = Object.assign({}, this.$route.query);
    delete query.forceRefresh;
    this.$router.replace({ query });


    if(this.toaster !== ""){
      this.$swal({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000000,
        type: "success",
        text: this.toaster,
        showCloseButton: true
      });
      this.$store.commit("setToaster", "");
    }
  }  
  
  private async refresh(){
    this.preloading = true;
    this.loading = true;
    var getWorkspacesParams = new GetWorkspacesParams(this.$store.state.authModule.userName, true);
    await this.$store.dispatch("preloadWorkspaces", getWorkspacesParams);
    this.preloading = false;
    await this.$store.dispatch("getWorkspaces", getWorkspacesParams);
    await this.getVmStates();
    this.loading = false;
  }
  
  private async openModalRemoveWorkspace(workspace: Workspace){
    var answer = await this.$swal({
      title:
        `Are you sure you want to remove ${workspace.name}? The budget allocation and all content in the workspace will be deleted!`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      allowOutsideClick: true
    });
    if(answer.value){
      this.removeWorkspace(workspace);
    } else {
      return;
    }
  }

  private async removeWorkspace(workspace: Workspace){
    this.$swal({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      type: "success",
      text: "Workspace is being removed, this can take a while"
    })
    workspace.workspaceStatus = "Deleting";
    var token = await this.authModule.getapiTokenPopup();
    workspace.remove(token);
    var addWorkspacesParams = new AddWorkspacesParams(this.$store.state.authModule.userName, this.userWorkspaces)
    this.$store.commit("addWorkspaces", addWorkspacesParams)
  }
}
